import { HTTPError } from '../../../support/errors';

import {
  MergeErrorCode,
  MERGE_ERROR_CODES,
  MergeApiErrorCode,
  RESOLUTION_STATES,
  ResolutionState,
} from './mergeResolution.types';

const INTERNAL_MESSAGE = 'The merge could not be computed.';

export class MergeApiError extends Error {
  constructor(
    public code: MergeErrorCode,
    public status: number,
    message?: string,
    // RESOLUTION_UNAVAILABLE only. `resolvedByName` is the winner's display
    // name, and is null when the server could not resolve one.
    public state?: ResolutionState,
    public resolvedByName?: string | null
  ) {
    super(message ?? INTERNAL_MESSAGE);
    this.name = 'MergeApiError';
  }
}

export const toMergeApiError = (err: unknown): MergeApiError => {
  if (err instanceof MergeApiError) {
    return err;
  }

  if (err instanceof HTTPError) {
    const code = err.body?.code;
    if (
      typeof code === 'string' &&
      MERGE_ERROR_CODES.includes(code as MergeApiErrorCode)
    ) {
      const message =
        typeof err.body?.message === 'string'
          ? err.body.message
          : INTERNAL_MESSAGE;
      // Anything outside the allowlist is dropped: an unknown State falls
      // back to the generic text, a non-string name to null.
      const state = RESOLUTION_STATES.find((s) => s === err.body?.State);
      const name = err.body?.ResolvedByName;
      return new MergeApiError(
        code as MergeApiErrorCode,
        err.status,
        message,
        state,
        typeof name === 'string' ? name : null
      );
    }

    if (err.status === 403) {
      return new MergeApiError(
        'FORBIDDEN',
        403,
        'You do not have access to this scenario.'
      );
    }

    if (err.status === 404) {
      return new MergeApiError('NOT_FOUND', 404, 'Submission not found.');
    }

    return new MergeApiError('INTERNAL_ERROR', err.status, INTERNAL_MESSAGE);
  }

  if (err instanceof DOMException && err.name === 'AbortError') {
    return new MergeApiError('ABORTED', 0, 'Request aborted.');
  }

  return new MergeApiError('NETWORK', 0, 'Could not reach the server.');
};
